import React from 'react'
import { useLocation } from 'react-router'
import { colors, fontSize, radius, spacing } from '../styles/theme'

interface State {
  error: Error | null
}

/**
 * Generic React error boundary. Without one, an uncaught render-time error anywhere in a
 * subtree unmounts the whole tree back to the nearest boundary (or the app root) and leaves a
 * blank page with nothing but a console warning — see this file's introduction alongside
 * TemplateEditorPage.tsx for the incident that motivated adding it.
 */
export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: spacing.lg }}>
          <div style={{ color: colors.error, fontWeight: 600, marginBottom: spacing.sm }}>
            Something went wrong.
          </div>
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: colors.errorLight,
            color: colors.error, padding: spacing.md, borderRadius: radius.sm, fontSize: fontSize.xs,
          }}>
            {this.state.error.message}
            {'\n\n'}
            {this.state.error.stack}
          </pre>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * ErrorBoundary keyed to the current route: React error boundaries don't reset on their own when
 * their children change, so without this, navigating away from a page that crashed would keep
 * showing the crashed page's error screen instead of rendering the new route. Remounting on every
 * pathname change clears it automatically.
 */
export function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
}
